import { EmptyState } from '@components/EmptyState';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { useWorkflowsByProgram } from '@hooks/useWorkflows';
import { useStore } from '@store/index';
import { AlertTriangle, FolderX, XCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { OrganizationCombobox } from '@/components/Header/OrganizationCombobox';
import { useOrganizationSwitcher } from '@/hooks/useOrganizationSwitcher';
import { removeOrgPrefix } from '@/utils/routes';

interface DataInitializerProps {
  children: React.ReactNode;
}

export const AppStateInitializer: React.FC<DataInitializerProps> = ({ children }) => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId?: string }>();
  const { isAuthenticated, organizationIds, programIds, selectedWorkflowId } = useStore();

  // Fetch organizations and programs together when authenticated
  const { isLoadingOrgAndPrograms, unauthorizedAccess, selectedProgramId } = useOrgAndPrograms();
  // Fetch workflows for selected program (needed for bulk create)
  const { isLoading: isLoadingWorkflow } = useWorkflowsByProgram(selectedProgramId || '');

  // Extract the org ID from URL (remove 'org-' prefix) and check access
  const urlOrgId = orgId ? removeOrgPrefix(orgId) : null;
  const hasOrgAccess = useMemo(() => {
    if (!urlOrgId) return true; // No org in URL, allow access
    if (isLoadingOrgAndPrograms) return true; // Don't check access while still loading
    return organizationIds.includes(urlOrgId);
  }, [urlOrgId, organizationIds, isLoadingOrgAndPrograms]);

  // Don't render anything if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Show loading spinner while fetching organizations and programs
  // Note: We only check isLoadingOrgAndPrograms here, not workflows yet
  if (isLoadingOrgAndPrograms) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center">
          <div className="rounded-full h-8 w-8 mx-auto">
            <img
              src="/images/company/hms-header-logo.png"
              alt="100ms logo"
              className=" animate-bounce"
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">Getting things ready…</p>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have access to the organization in the URL
  if (unauthorizedAccess?.isUnauthorized || !hasOrgAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <EmptyState
          title="Access Denied"
          description="You do not have access to this organization. Please contact your administrator if you believe this is an error."
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{
            label: 'Go to Home',
            onClick: () => navigate('/'),
          }}
        />
      </div>
    );
  }

  // Check for no organizations BEFORE checking workflows
  if (organizationIds.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <EmptyState
          title="No Organizations Found"
          description="It looks like your account is not associated with any organizations. Please contact your administrator to get access."
          icon={AlertTriangle}
          iconClassName="text-muted-foreground"
          action={{
            label: 'Sign Out',
            onClick: () => navigate('/logout'),
            variant: 'outline',
          }}
        />
      </div>
    );
  }

  // Check for no programs BEFORE checking workflows
  // Show org switcher if user has multiple organizations
  if (programIds.length === 0) {
    return <NoProgramsEmptyState />;
  }

  // Now check for workflow loading (only relevant when user has programs)
  if (isLoadingWorkflow || !selectedWorkflowId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center">
          <div className="rounded-full h-8 w-8 mx-auto">
            <img
              src="/images/company/hms-header-logo.png"
              alt="100ms logo"
              className=" animate-bounce"
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">Getting things ready…</p>
        </div>
      </div>
    );
  }

  // Once data is loaded, render children
  return <>{children}</>;
};

AppStateInitializer.displayName = 'AppStateInitializer';

/**
 * Empty state component shown when user has no programs in the current organization.
 * Includes an org switcher if user has access to multiple organizations.
 */
const NoProgramsEmptyState: React.FC = () => {
  const navigate = useNavigate();
  const { getOrganizationsArray, selectedOrganizationId, getSelectedOrganization } = useStore();
  const { switchToOrganization } = useOrganizationSwitcher();

  const organizations = getOrganizationsArray();
  const hasMultipleOrgs = organizations.length > 1;
  const selectedOrg = getSelectedOrganization();
  const orgName = selectedOrg?.name || 'this organization';

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <EmptyState
        title="No Programs Assigned"
        description={`You are not assigned any programs in ${orgName}. Please contact your administrator to get access to atleast one program.`}
        icon={FolderX}
        iconClassName="text-muted-foreground"
        action={
          hasMultipleOrgs
            ? undefined
            : {
                label: 'Log out',
                onClick: () => navigate('/logout'),
              }
        }
      >
        {hasMultipleOrgs && (
          <div className="flex flex-col items-center gap-2 mt-4">
            <span className="text-sm text-muted-foreground">Switch to different organization:</span>
            <div className="border rounded-md px-3 py-2 bg-background">
              <OrganizationCombobox
                organizations={organizations}
                selectedOrganizationId={selectedOrganizationId}
                onOrganizationChange={switchToOrganization}
              />
            </div>
          </div>
        )}
      </EmptyState>
    </div>
  );
};
